import { createClient, type Session, type User } from "@supabase/supabase-js";
import { create } from "zustand";

// Supabase クライアントの初期化
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

type AuthState = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isSystemAdmin: boolean;
  error: string | null;

  // アクション
  initialize: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkSystemAdmin: () => boolean;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  isLoading: true,
  isSystemAdmin: false,
  error: null,

  initialize: async () => {
    try {
      // 現在のセッションを取得
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }

      if (session) {
        const isAdmin = get().checkSystemAdmin();
        set({
          user: session.user,
          session,
          isSystemAdmin: isAdmin,
          isLoading: false,
        });
      } else {
        set({ isLoading: false });
      }

      // セッション変更を監視
      supabase.auth.onAuthStateChange((_event, session) => {
        if (session) {
          const isAdmin = get().checkSystemAdmin();
          set({
            user: session.user,
            session,
            isSystemAdmin: isAdmin,
          });
        } else {
          set({
            user: null,
            session: null,
            isSystemAdmin: false,
          });
        }
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "認証エラーが発生しました",
        isLoading: false,
      });
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ error: null });
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/admin`,
        },
      });
      if (error) {
        throw error;
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "ログインに失敗しました",
      });
    }
  },

  signInWithPassword: async (email: string, password: string) => {
    try {
      set({ error: null, isLoading: true });
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        throw error;
      }
      if (data.session) {
        const isAdmin = get().checkSystemAdmin();
        set({
          user: data.user,
          session: data.session,
          isSystemAdmin: isAdmin,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "ログインに失敗しました",
        isLoading: false,
      });
    }
  },

  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      set({
        user: null,
        session: null,
        isSystemAdmin: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "ログアウトに失敗しました",
      });
    }
  },

  checkSystemAdmin: () => {
    const { user } = get();
    if (!user?.email) {
      return false;
    }

    // 環境変数から管理者メールアドレスリストを取得
    // ビルド時に埋め込まれるため、VITE_プレフィックスが必要
    const adminEmails = (import.meta.env.VITE_ADMIN_EMAILS as string)?.split(",") ?? [];
    return adminEmails.includes(user.email);
  },
}));
